import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HardHat, ArrowLeft, ShieldCheck, ChartBar, UsersThree, Lightning } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

const FEATURES = [
  { icon: ShieldCheck, text: "Verified supplier network" },
  { icon: ChartBar,    text: "Full platform analytics" },
  { icon: UsersThree,  text: "Manage buyers & enquiries" },
  { icon: Lightning,   text: "Real-time lead management" },
];

function UnderlineInput({ label, id, type = "text", placeholder, value, onChange, onKeyDown, testId, autoComplete }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-bold tracking-[0.12em] uppercase mb-2" style={{ color: "#999" }}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          autoComplete={autoComplete}
          data-testid={testId}
          className="w-full bg-transparent text-neutral-900 text-sm py-2.5 outline-none placeholder-neutral-400"
          style={{ borderBottom: `2px solid ${focused ? "#FF7A00" : "#D8D2C8"}`, transition: "border-color 0.2s" }}
        />
      </div>
    </div>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) { toast.error("Fill all fields"); return; }
    setLoading(true);
    try {
      const user = await login(loginEmail, loginPassword);
      if (user.role === "admin") {
        toast.success("Welcome back!");
        navigate("/admin");
        setLoading(false);
        return;
      }
      logout();
      if (user.role === "buyer") {
        toast.error("Buyer accounts use the buyer sign-in page.");
      } else if (user.role === "agent") {
        toast.error("Staff accounts use the staff sign-in page.");
      } else {
        toast.error("Use the supplier portal for supplier accounts.");
      }
      setLoading(false);
      return;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#F7F4EF" }}>

      {/* ── LEFT BRANDING PANEL ─────────────────────────── */}
      <div
        className="hidden lg:flex flex-col justify-between w-[440px] shrink-0 px-12 py-14 relative overflow-hidden"
        style={{ background: "linear-gradient(160deg, #111 0%, #1c1c1c 100%)" }}
      >
        {/* Grid texture */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "36px 36px",
          }}
        />

        {/* Top — logo */}
        <div className="relative flex items-center gap-2.5">
          <HardHat size={26} weight="bold" style={{ color: "#FF7A00" }} />
          <span className="text-white font-black tracking-tight text-lg">SCAFFOINDIA</span>
        </div>

        {/* Middle — headline + features */}
        <div className="relative">
          <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-4" style={{ color: "#FF7A00" }}>
            Admin portal
          </p>
          <h2
            className="font-black text-white leading-none tracking-tighter mb-8"
            style={{ fontSize: "clamp(1.9rem, 3vw, 2.6rem)" }}
          >
            Platform
            <br />
            <span style={{ color: "#FF7A00" }}>command</span>
            <br />
            centre.
          </h2>
          <ul className="space-y-4">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: "rgba(255,122,0,0.15)" }}
                >
                  <Icon size={16} weight="bold" style={{ color: "#FF7A00" }} />
                </div>
                <span className="text-sm font-medium" style={{ color: "#ccc" }}>{text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom — other portal links */}
        <div className="relative space-y-2">
          <p className="text-xs" style={{ color: "#666" }}>Other portals:</p>
          <div className="flex flex-col gap-1">
            <Link to="/account/login" className="text-xs font-semibold hover:opacity-80 transition-opacity" style={{ color: "#888" }}>
              Buyer sign in →
            </Link>
            <Link to="/supplier/login" className="text-xs font-semibold hover:opacity-80 transition-opacity" style={{ color: "#888" }}>
              Supplier portal →
            </Link>
          </div>
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ────────────────────────────── */}
      <div className="flex-1 flex flex-col min-h-screen overflow-y-auto" style={{ background: "#F7F4EF" }}>

        {/* Mobile top bar */}
        <div className="flex items-center justify-between px-6 py-5 lg:hidden border-b" style={{ borderColor: "#E8E3D9" }}>
          <div className="flex items-center gap-2">
            <HardHat size={22} weight="bold" style={{ color: "#FF7A00" }} />
            <span className="text-neutral-900 font-black tracking-tight">SCAFFOINDIA</span>
          </div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-1 text-xs font-semibold"
            style={{ color: "#999" }}
            data-testid="back-to-home-btn"
          >
            <ArrowLeft size={13} weight="bold" /> Home
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-14 sm:px-10 lg:px-16 xl:px-20">
          <div className="w-full max-w-md">

            {/* Desktop back link */}
            <button
              type="button"
              onClick={() => navigate("/")}
              className="hidden lg:inline-flex items-center gap-1.5 text-xs font-semibold mb-10 transition-opacity hover:opacity-60"
              style={{ color: "#aaa" }}
              data-testid="back-to-home-btn"
            >
              <ArrowLeft size={13} weight="bold" /> Back to home
            </button>

            {/* Heading */}
            <div className="mb-10">
              {/* Admin badge */}
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase mb-5"
                style={{ background: "rgba(255,122,0,0.1)", color: "#FF7A00", border: "1px solid rgba(255,122,0,0.2)" }}
              >
                <ShieldCheck size={12} weight="bold" />
                Admin access only
              </div>
              <h1
                className="font-black text-neutral-900 leading-tight tracking-tighter"
                style={{ fontSize: "clamp(1.8rem, 3vw, 2.4rem)" }}
              >
                Welcome back.
              </h1>
              <p className="mt-2 text-sm" style={{ color: "#999" }}>
                Sign in to your administrator account
              </p>
            </div>

            {/* Form */}
            <div className="space-y-7">
              <UnderlineInput
                label="Email"
                id="admin-email"
                type="email"
                placeholder="your@email.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                autoComplete="email"
                testId="login-email-input"
              />
              <UnderlineInput
                label="Password"
                id="admin-password"
                type="password"
                placeholder="Enter password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                autoComplete="current-password"
                testId="login-password-input"
              />

              <button
                type="button"
                data-testid="login-submit-btn"
                onClick={handleLogin}
                disabled={loading}
                className="w-full h-12 rounded-full font-black text-sm tracking-wide transition-opacity disabled:opacity-60 hover:opacity-90"
                style={{ background: "#FF7A00", color: "#fff" }}
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </div>

            {/* Mobile portal links */}
            <div className="mt-10 pt-6 border-t flex flex-col gap-2 lg:hidden" style={{ borderColor: "#E8E3D9" }}>
              <p className="text-xs mb-1" style={{ color: "#bbb" }}>Other portals:</p>
              <Link to="/account/login" className="text-sm font-semibold" style={{ color: "#FF7A00" }} data-testid="link-buyer-login">
                Buyer sign in →
              </Link>
              <Link to="/supplier/login" className="text-sm font-semibold" style={{ color: "#FF7A00" }} data-testid="link-supplier-portal">
                Supplier portal →
              </Link>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
